/*
 * Copyright (c) Nexign, JSC, 2020-2025.
 */

/**
 * The guard constants for pages access
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import { AuthorityRoleEnum, EUserType } from '../../../../pages/PageAuth/constants/authConstants';
import { IAccess } from '../../types/guardTypes';
import { AccessComponentKeyEnum, ConnectionSourceEnum, EGuardAuthUserFlag, FeatureNameEnum } from './guardConstants';

const PageAccess: IAccess = {
    [AccessComponentKeyEnum.PagePersonalAccount]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageSubscriberMobile]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageSubscriberId]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageHistoryOrder]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageOrderId]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.ORDER_ID,
    },
    [AccessComponentKeyEnum.PageOffers]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageNotFound]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageBills]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageExpensesAnalyzer]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageMoneyTransfer]: {
        connectionSource: ConnectionSourceEnum.route,
        role: [
            AuthorityRoleEnum.ROLE_CASH_TRANSFER,
            AuthorityRoleEnum.ROLE_AGREEMENT_COORDINATOR,
            AuthorityRoleEnum.ROLE_CUSTOMER_COORDINATOR,
            AuthorityRoleEnum.ROLE_HOLDING_COORDINATOR,
            AuthorityRoleEnum.ROLE_SUB_DIV_COORDINATOR,
        ],
    },
    [AccessComponentKeyEnum.PageLabelsImport]: {
        authUserData: {
            isEnabled: false,
            name: EGuardAuthUserFlag.IS_SUBSCRIBER,
        },
        connectionSource: ConnectionSourceEnum.route,
        role: [
            AuthorityRoleEnum.ROLE_COORDINATOR_USER,
            AuthorityRoleEnum.ROLE_TAGS_IMPORT,
        ],
    },
    [AccessComponentKeyEnum.PageSettings]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageSettingsAutoReports]: {
        connectionSource: ConnectionSourceEnum.route,
        role: [AuthorityRoleEnum.ROLE_PRIVILEGED_USER],
    },
    [AccessComponentKeyEnum.PageSettingsLabels]: {
        connectionSource: ConnectionSourceEnum.route,
        role: [
            AuthorityRoleEnum.ROLE_COORDINATOR_USER,
            AuthorityRoleEnum.ROLE_TAGS_IMPORT,
        ],
    },
    [AccessComponentKeyEnum.PageSettingsPositions]: {
        connectionSource: ConnectionSourceEnum.route,
        role: [AuthorityRoleEnum.ROLE_COORDINATOR_USER],
    },
    [AccessComponentKeyEnum.PageSettingsStatistics]: {
        connectionSource: ConnectionSourceEnum.route,
        // https://jira.nexign.com/browse/BBPORTAL-9141
        role: [],
    },
    [AccessComponentKeyEnum.PageProducts]: {
        connectionSource: ConnectionSourceEnum.route,
        userTypeId: {
            isAllowed: false,
            id: [EUserType.SACS_ID_READONLY],
        },
    },
    [AccessComponentKeyEnum.PageWizardAdditionalAccounts]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageWizardBillsDocs]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageWizardCallsDetail]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageWizardChangeLock]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageWizardExpensesDetail]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageWizardMultiSearch]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.WIZARD_MULTI_SEARCH,
    },
    [AccessComponentKeyEnum.TitleButtonWizardMultiSearch]: {
        featureName: FeatureNameEnum.WIZARD_MULTI_SEARCH,
    },
    [AccessComponentKeyEnum.TableMsisdnMultiSearch]: {
        featureName: FeatureNameEnum.WIZARD_MULTI_SEARCH,
    },
    [AccessComponentKeyEnum.PageReportsConstructor]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.REPORTS_CONSTRUCTOR,
    },
    [AccessComponentKeyEnum.PageCollectiveProduct]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageNews]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.NEWS,
    },
    [AccessComponentKeyEnum.PageWizardChangeRole]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.CHANGE_ROLE_NEW_UI,
    },

    // PageAccountInfo

    [AccessComponentKeyEnum.PageAccountInfo]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    // Вкладка финансы зависит от рычага мультибалансов, да
    [AccessComponentKeyEnum.PageAccountInfoFinances]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.MULTI_BALANCES,
    },
    [AccessComponentKeyEnum.PageAccountInfoGeneral]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageAccountInfoShipping]: {
        connectionSource: ConnectionSourceEnum.route,
    },

    // PageAccountCashback
    [AccessComponentKeyEnum.PageAccountCashback]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.CASHBACK,
        featureVersion: [2, 3, 4],
    },

    // PageAutoReportId
    [AccessComponentKeyEnum.PageAutoReportId]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.AUTO_REPORT_ID,
    },

    // Page Inquiries
    [AccessComponentKeyEnum.PageHistoryInquiry]: {
        connectionSource: ConnectionSourceEnum.route,
    },
    [AccessComponentKeyEnum.PageInquiryInfo]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.INQUIRY_INFO,
    },

    // Page Payments
    [AccessComponentKeyEnum.PagePayments]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.PAYMENTS,
    },

    [AccessComponentKeyEnum.PageWizardAdditionalReports]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.CDM_ADDITIONAL_REPORTS,
    },

    [AccessComponentKeyEnum.PageWizardCollationReport]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.CDM_ADDITIONAL_REPORTS,
    },

    [AccessComponentKeyEnum.FastInquiryCreate]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.MAIN_OPERATIONS,
    },

    [AccessComponentKeyEnum.FastAdvanceInvoice]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.MAIN_OPERATIONS,
    },
    [AccessComponentKeyEnum.FastPromisedPayment]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.MAIN_OPERATIONS,
    },
    [AccessComponentKeyEnum.WizardFastBillsDocs]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.MAIN_OPERATIONS,
    },
    [AccessComponentKeyEnum.WizardFastReportDocs]: {
        connectionSource: ConnectionSourceEnum.route,
        featureName: FeatureNameEnum.MAIN_OPERATIONS,
    },
};

export default PageAccess;
